import {
  CheckIcon,
  ChevronDownIcon,
  Cross2Icon,
  Pencil1Icon,
  PlusIcon,
} from '@radix-ui/react-icons';
import * as RdxPopover from '@radix-ui/react-popover';
import { Controller } from 'react-hook-form';
import { REFERRER_KIND_VALUES } from '@clickersphere/core/enums/ReferrerKindEnum';
import cn from '../../../app/utils/cn';
import { Button } from '../Button';
import { IconButton } from '../IconButton';
import { Input } from '../Input';
import Select from '../Select';
import { TextButton } from '../TextButton';
import { referrerKindLabelKeys } from './referrerEnumLabels';
import { useReferrerComboboxController } from './useReferrerComboboxController';

interface IReferrerComboboxProps {
  value?: string | null;
  onChange: (referrerId: string | null, referrerName?: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

export default function ReferrerCombobox({
  value,
  onChange,
  disabled,
  placeholder,
  className,
}: IReferrerComboboxProps) {
  const {
    t,
    form,
    open,
    search,
    showForm,
    isEditing,
    isFormBusy,
    isDeleteConfirming,
    isReferrersLoading,
    selectedReferrer,
    filteredReferrers,
    exactMatch,
    setSearch,
    handleOpenChange,
    handleSelect,
    handleClear,
    openCreateForm,
    openEditForm,
    toggleDeleteConfirm,
    handleSubmit,
    resetForm,
  } = useReferrerComboboxController({ value, onChange });

  return (
    <div className={cn('w-full', className)}>
      <RdxPopover.Root open={open} onOpenChange={handleOpenChange}>
        <div className="relative flex w-full items-center">
          <RdxPopover.Trigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-expanded={open}
              aria-haspopup="listbox"
              aria-label={t('referrer.combobox.triggerAriaLabel')}
              className={cn(
                'bg-surface-input border-border-input text-text-primary flex h-10 w-full items-center justify-between gap-2 rounded border-2 border-solid px-2 py-1.5 text-sm shadow-md outline-none',
                'focus:border-green-600 dark:focus:border-green-400',
                'disabled:cursor-not-allowed disabled:opacity-50',
                selectedReferrer && 'pr-10',
              )}
            >
              <span className="min-w-0 flex-1 truncate text-left">
                {selectedReferrer ? (
                  selectedReferrer.name
                ) : (
                  <span className="text-text-tertiary">
                    {placeholder ?? t('referrer.combobox.placeholder')}
                  </span>
                )}
              </span>
              <ChevronDownIcon className="text-text-tertiary shrink-0" />
            </button>
          </RdxPopover.Trigger>

          {selectedReferrer && !disabled && (
            <IconButton
              size="md"
              onClick={handleClear}
              aria-label={t('referrer.combobox.clear')}
              className="absolute right-7"
            >
              <Cross2Icon className="h-4 w-4" />
            </IconButton>
          )}
        </div>

        <RdxPopover.Portal>
          <RdxPopover.Content
            className="bg-surface-card border-border-default z-50 mt-1 max-h-[70vh] w-[var(--radix-popover-trigger-width)] min-w-[260px] overflow-y-auto rounded-lg border p-2 shadow-lg"
            sideOffset={4}
            align="start"
          >
            {!showForm && (
              <>
                <div className="mb-2">
                  <Input
                    name="referrer-search"
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('referrer.combobox.searchPlaceholder')}
                    aria-label={t('referrer.combobox.searchPlaceholder')}
                  />
                </div>

                <ul
                  role="listbox"
                  className="flex max-h-60 flex-col gap-0.5 overflow-y-auto"
                >
                  {isReferrersLoading && (
                    <li className="text-text-tertiary p-2 text-xs">
                      {t('referrer.combobox.loading')}
                    </li>
                  )}

                  {!isReferrersLoading && filteredReferrers.length === 0 && (
                    <li className="text-text-tertiary p-2 text-xs">
                      {t('referrer.combobox.empty')}
                    </li>
                  )}

                  {filteredReferrers.map((referrer) => {
                    const checked = referrer.id === value;
                    return (
                      <li
                        key={referrer.id}
                        role="option"
                        aria-selected={checked}
                        className="hover:bg-hover-subtle dark:hover:bg-hover-medium flex items-center gap-1 rounded"
                      >
                        <button
                          type="button"
                          onClick={() => handleSelect(referrer)}
                          className="text-text-primary flex min-h-10 flex-1 items-center justify-between gap-2 px-2 py-2 text-left text-sm"
                        >
                          <span className="flex min-w-0 flex-col">
                            <span className="truncate">{referrer.name}</span>
                            <span className="text-text-tertiary truncate text-xs">
                              {t(referrerKindLabelKeys[referrer.kind])}
                            </span>
                          </span>
                          {checked && (
                            <CheckIcon className="text-text-primary shrink-0" />
                          )}
                        </button>
                        <IconButton
                          size="md"
                          onClick={() => openEditForm(referrer)}
                          aria-label={t('referrer.combobox.editAriaLabel', {
                            name: referrer.name,
                          })}
                          className="shrink-0"
                        >
                          <Pencil1Icon className="h-3.5 w-3.5" />
                        </IconButton>
                      </li>
                    );
                  })}
                </ul>

                <div className="border-border-default mt-2 flex flex-col border-t pt-2">
                  <TextButton
                    onClick={openCreateForm}
                    disabled={!search.trim() && exactMatch}
                    className="text-text-primary hover:bg-hover-subtle dark:hover:bg-hover-medium min-h-10 w-full justify-start gap-2 px-2 py-2"
                  >
                    <PlusIcon className="shrink-0" />
                    <span className="truncate">
                      {search.trim()
                        ? t('referrer.combobox.createNamed', {
                            name: search.trim(),
                          })
                        : t('referrer.combobox.createNew')}
                    </span>
                  </TextButton>
                </div>
              </>
            )}

            {showForm && (
              <div className="flex flex-col gap-3">
                <h3 className="text-text-primary text-sm font-semibold">
                  {isEditing
                    ? t('referrer.combobox.editTitle')
                    : t('referrer.combobox.createTitle')}
                </h3>

                <Input
                  type="text"
                  placeholder={t('referrer.combobox.namePlaceholder')}
                  aria-label={t('referrer.combobox.namePlaceholder')}
                  maxLength={100}
                  disabled={isFormBusy}
                  {...form.register('name')}
                />

                <Controller
                  control={form.control}
                  name="kind"
                  render={({
                    field: { value: kind, onChange: onKindChange },
                  }) => (
                    <Select
                      placeholder={t('referrer.combobox.kindPlaceholder')}
                      options={REFERRER_KIND_VALUES.map((option) => ({
                        value: option,
                        text: t(referrerKindLabelKeys[option]),
                      }))}
                      value={kind}
                      onChange={onKindChange}
                      disabled={isFormBusy}
                    />
                  )}
                />

                <Input
                  type="tel"
                  autoComplete="tel"
                  placeholder={t('referrer.combobox.phonePlaceholder')}
                  aria-label={t('referrer.combobox.phonePlaceholder')}
                  maxLength={30}
                  disabled={isFormBusy}
                  {...form.register('phone')}
                />

                <Input
                  type="text"
                  placeholder={t('referrer.combobox.instagramPlaceholder')}
                  aria-label={t('referrer.combobox.instagramPlaceholder')}
                  maxLength={30}
                  disabled={isFormBusy}
                  {...form.register('instagram')}
                />

                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button
                    color="ghost"
                    labelPath="referrer.combobox.cancel"
                    onClick={resetForm}
                    disabled={isFormBusy}
                  />
                  <Button
                    color="primary"
                    labelPath={
                      isEditing
                        ? 'referrer.combobox.save'
                        : 'referrer.combobox.confirm'
                    }
                    onClick={handleSubmit}
                    disabled={isFormBusy}
                    isLoading={isFormBusy}
                  />
                </div>

                {isEditing && (
                  <Button
                    color="danger"
                    labelPath={
                      isDeleteConfirming
                        ? 'referrer.combobox.confirmDelete'
                        : 'referrer.combobox.delete'
                    }
                    onClick={toggleDeleteConfirm}
                    disabled={isFormBusy}
                  />
                )}
              </div>
            )}
          </RdxPopover.Content>
        </RdxPopover.Portal>
      </RdxPopover.Root>
    </div>
  );
}
